import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const TurnoverRateChart = ({ data, dateRange }) => {
  // Calculate turnover rate for each product
  const chartData = data?.slice(0, 10)?.map(item => {
    const stockOut = parseFloat(item?.stock_out_30d) || 0;
    const avgQuantity = parseFloat(item?.quantity) || 1;
    const turnoverRate = avgQuantity > 0 ? (stockOut / avgQuantity)?.toFixed(2) : 0;

    return {
      name: item?.name?.length > 20 ? item?.name?.substring(0, 20) + '...' : item?.name,
      turnoverRate: parseFloat(turnoverRate),
      stockOut: stockOut,
      category: item?.category
    };
  })?.sort((a, b) => b?.turnoverRate - a?.turnoverRate);

  // Color coding based on turnover rate
  const getBarColor = (rate) => {
    if (rate >= 2) return 'var(--color-success)'; // High turnover (good)
    if (rate >= 1) return 'var(--color-warning)'; // Medium turnover
    return 'var(--color-error)'; // Low turnover (needs attention)
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-foreground mb-2">{data?.name}</p>
          <p className="text-sm text-muted-foreground">Category: {data?.category}</p>
          <p className="text-sm text-primary font-medium">Turnover Rate: {data?.turnoverRate}x</p>
          <p className="text-sm text-muted-foreground">Stock Out (30d): {data?.stockOut}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Inventory Turnover Rates
          </h2>
          <p className="text-sm text-muted-foreground">
            Top 10 products by turnover rate (last {dateRange} days)
          </p>
        </div>
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="BarChart3" size={20} color="var(--color-primary)" />
        </div>
      </div>

      {chartData?.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No turnover data available</p>
          </div>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                height={80}
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Turnover Rate (times)', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)' }}
                tick={{ fill: 'var(--color-muted-foreground)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="turnoverRate" radius={[8, 8, 0, 0]}>
                {chartData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry?.turnoverRate)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'var(--color-success)' }}></div>
              <span className="text-muted-foreground">High Turnover (≥2x)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'var(--color-warning)' }}></div>
              <span className="text-muted-foreground">Medium Turnover (1-2x)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'var(--color-error)' }}></div>
              <span className="text-muted-foreground">Low Turnover (&lt;1x)</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TurnoverRateChart;